import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ArrowLeft, Loader2, Star, ChevronRight, Shield, FileDown } from "lucide-react";
import { toast } from "sonner";
import { exportRivalDossierPdf } from "@/lib/export";
import { fmtNum, fmtPts } from "@/lib/datasetFmt";

const KPI = ({ label, value, testId }) => (
  <Card className="border-slate-200" data-testid={testId}>
    <CardContent className="p-4 text-center">
      <div className="text-2xl font-black text-purple-800">{value}</div>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-1">{label}</div>
    </CardContent>
  </Card>
);

const SORTS = [
  ["ranking", "Ranking"],
  ["puntuacion", "Puntuación"],
  ["goles", "Goles"],
  ["partidos", "Partidos"],
  ["minutos", "Minutos"],
  ["anio", "Año nacimiento"],
];

export default function RivalTeamDashboard() {
  const { name } = useParams();
  const navigate = useNavigate();
  const equipoEf = new URLSearchParams(window.location.search).get("ef") || "";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState("ranking");
  const [posFilter, setPosFilter] = useState("any");

  useEffect(() => {
    api.get(`/rival-dataset/rival-dashboard?name=${encodeURIComponent(name)}${equipoEf ? `&equipo_ef=${encodeURIComponent(equipoEf)}` : ""}`)
      .then((r) => setData(r.data))
      .catch((err) => toast.error(formatApiError(err)))
      .finally(() => setLoading(false));
  }, [name, equipoEf]);

  const positions = useMemo(
    () => [...new Set((data?.players || []).map((p) => p.posicion).filter(Boolean))].sort(),
    [data]
  );

  const sorted = useMemo(() => {
    let ps = [...(data?.players || [])];
    if (posFilter !== "any") ps = ps.filter((p) => p.posicion === posFilter);
    const val = (p) => {
      switch (sortBy) {
        case "puntuacion": return -(p.puntuacion ?? -Infinity);
        case "goles": return -(p.goles_total ?? -1);
        case "partidos": return -(p.jugados_total ?? -1);
        case "minutos": return -((p.minutos_disponibles === "Sí" && p.minutos_total_app) || -1);
        case "anio": return -(p.anio_nacimiento ?? 0);
        default: return p.ranking_general ?? Infinity;
      }
    };
    ps.sort((a, b) => val(a) - val(b));
    return ps;
  }, [data, sortBy, posFilter]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24" data-testid="rival-team-loading">
        <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
      </div>
    );
  }
  if (!data) return <p className="text-sm text-slate-400 py-10 text-center">Rival no encontrado.</p>;

  const k = data.kpis;
  const top5 = (data.players || []).filter((p) => p.ranking_general != null).slice(0, 5);

  const goPlayer = (p) => navigate(`/rivals/jugador/${p.jugador_id}`);

  return (
    <div className="space-y-5" data-testid="rival-team-dashboard">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <Button variant="ghost" onClick={() => navigate(-1)} data-testid="rival-team-back">
          <ArrowLeft className="h-4 w-4 mr-2" /> Volver
        </Button>
        <Button
          variant="outline"
          onClick={() => exportRivalDossierPdf(data.rival, equipoEf)}
          className="border-purple-300 text-purple-700 hover:bg-purple-50 font-bold"
          data-testid="rival-team-dossier-pdf"
        >
          <FileDown className="h-4 w-4 mr-2" /> Dossier PDF
        </Button>
      </div>

      {/* Header */}
      <div className="rounded-2xl text-white p-6" style={{ background: "linear-gradient(120deg, #3d1758, #5B2383)" }} data-testid="rival-team-header">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-white/15 border border-white/25 grid place-items-center shrink-0">
            <Shield className="h-7 w-7" style={{ color: "#D9B300" }} />
          </div>
          <div className="min-w-0">
            <h1 className="text-2xl sm:text-3xl font-black leading-tight" style={{ fontFamily: "Nunito" }} data-testid="rival-team-name">
              {data.rival}
            </h1>
            <div className="text-sm text-purple-100 mt-0.5">
              Rival de {k.equipos_ef.join(" / ") || "—"}
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 mt-4">
          <Badge className="bg-white/20 text-white border-0">{k.jugadores} jugadores analizados</Badge>
          <Badge className="bg-white/20 text-white border-0">{k.temporadas} temporadas</Badge>
          <Badge className="border-0 text-black font-bold" style={{ background: "#D9B300" }}>
            <Star className="h-3 w-3 mr-1" /> {k.veces_destacado} veces destacado
          </Badge>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-3 gap-3">
        <KPI label="Jugadores" value={fmtNum(k.jugadores)} testId="rival-team-kpi-jugadores" />
        <KPI label="Partidos" value={fmtNum(k.partidos)} testId="rival-team-kpi-partidos" />
        <KPI label="Goles" value={fmtNum(k.goles)} testId="rival-team-kpi-goles" />
        <KPI label="Minutos" value={fmtNum(k.minutos)} testId="rival-team-kpi-minutos" />
        <KPI label="Amarillas" value={fmtNum(k.amarillas)} testId="rival-team-kpi-amarillas" />
        <KPI label="Rojas" value={fmtNum(k.rojas)} testId="rival-team-kpi-rojas" />
      </div>

      {/* Top players */}
      <div>
        <h2 className="text-base md:text-lg font-black mb-3" style={{ fontFamily: "Nunito" }}>
          Jugadores destacados
        </h2>
        <div className="space-y-2">
          {top5.map((p, i) => (
            <Card
              key={p.jugador_id}
              onClick={() => goPlayer(p)}
              className="border-slate-200 hover:border-purple-400 hover:shadow-md transition cursor-pointer"
              data-testid={`rival-team-top-${p.jugador_id}`}
            >
              <CardContent className="p-4 flex items-center gap-3">
                <div className="w-9 h-9 rounded-full grid place-items-center font-black text-sm text-white shrink-0" style={{ background: i === 0 ? "#D9B300" : "#5B2383" }}>
                  {i + 1}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-bold text-slate-900 truncate">{p.jugador}</div>
                  <div className="text-xs text-slate-500">
                    {p.anio_nacimiento || "—"} · {p.posicion || "Sin posición"} · Ranking #{p.ranking_general}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {fmtNum(p.jugados_total)} PJ · {fmtNum(p.titular_total)} titular · {fmtNum(p.goles_total)} goles
                    {p.minutos_disponibles === "Sí" && p.minutos_total_app != null && ` · ${fmtNum(p.minutos_total_app)} min`}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-black" style={{ color: "#D9B300" }}>{fmtPts(p.puntuacion)}</div>
                  <div className="text-[9px] uppercase tracking-widest text-slate-400">Puntos</div>
                </div>
                <ChevronRight className="h-4 w-4 text-slate-300 shrink-0" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Full roster */}
      <div>
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <h2 className="text-base md:text-lg font-black" style={{ fontFamily: "Nunito" }}>
            Plantilla analizada
          </h2>
          <div className="flex gap-2">
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="h-8 text-xs w-36" data-testid="rival-team-sort">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SORTS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={posFilter} onValueChange={setPosFilter}>
              <SelectTrigger className="h-8 text-xs w-36" data-testid="rival-team-pos-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="any">Todas las posiciones</SelectItem>
                {positions.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <Card className="border-slate-200">
          <CardContent className="p-0">
            <ul className="divide-y divide-slate-100">
              {sorted.map((p) => (
                <li
                  key={p.jugador_id}
                  onClick={() => goPlayer(p)}
                  className="flex items-center gap-3 p-3.5 hover:bg-purple-50/40 cursor-pointer"
                  data-testid={`rival-team-player-${p.jugador_id}`}
                >
                  <div className="w-9 h-9 rounded-lg bg-purple-100 text-purple-800 grid place-items-center font-black text-xs shrink-0">
                    {p.ranking_general != null ? `#${p.ranking_general}` : "—"}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-slate-900 truncate text-sm">{p.jugador}</div>
                    <div className="text-xs text-slate-500">
                      {p.posicion || "Sin posición"} · {p.anio_nacimiento || "—"} · {fmtNum(p.jugados_total)} PJ · {fmtNum(p.goles_total)} goles
                    </div>
                  </div>
                  {p.veces_destacado > 0 && (
                    <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-purple-100 text-purple-800 text-[11px] font-bold shrink-0">
                      <Star className="h-3 w-3" /> {p.veces_destacado}×
                    </span>
                  )}
                  <ChevronRight className="h-4 w-4 text-slate-300 shrink-0" />
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
